import {
	listAnimalsByOrganizationQueryOptions,
	listHerdsByOrganizationQueryOptions,
} from "@packages/api-client/hooks/livestockController";
import type {
	CreateAnimalMutationRequest,
	UpdateAnimalMutationRequest,
} from "@packages/api-client/types/livestockController";
import type { LivestockHerdWithStats } from "@packages/shared-types/livestock";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
	useCreateAnimal,
	useCreateCalving,
	useDeleteAnimal,
	useDeleteOperation,
} from "@/api/livestock";
import { PageHeader } from "@/components/PageHeader";
import {
	AnimalCreatedPanel,
	type CreatedAnimalEntry,
} from "./-components/AnimalCreatedPanel";
import {
	AnimalForm,
	type AnimalFormSubmitOptions,
} from "./-components/AnimalForm";
import { undoCreatedAnimal } from "./-components/animalUndo";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/livestock/$establishmentId/animals/new",
)({
	staticData: { offline: "ready" },
	// `?tag=` prefills the tag field — used by the weighing session's
	// unknown-tag "create animal?" shortcut so the typed brinco carries over.
	// `?herd=` preselects the lot — used by the herd create flow's "Add animals
	// to this herd" so the new herd is already chosen.
	validateSearch: (
		search: Record<string, unknown>,
	): { tag?: string; herd?: string } => ({
		tag: typeof search.tag === "string" && search.tag ? search.tag : undefined,
		herd:
			typeof search.herd === "string" && search.herd ? search.herd : undefined,
	}),
	loader: ({ context, params }) => {
		// Fire-and-forget prefetch: if cached (online or from IDB), component sees
		// data immediately. If not cached offline, form still renders with empty options.
		context.queryClient.ensureQueryData({
			...listHerdsByOrganizationQueryOptions(params.orgId, {
				establishmentId: params.establishmentId,
			}),
			staleTime: Number.POSITIVE_INFINITY,
		});
		context.queryClient.ensureQueryData({
			...listAnimalsByOrganizationQueryOptions(params.orgId, {
				establishmentId: params.establishmentId,
				limit: "1000",
			}),
			staleTime: Number.POSITIVE_INFINITY,
		});
	},
	component: NewAnimalPage,
});

/** Values carried into the next blank form while "Repetir…" is on. */
type CarriedValues = {
	breed: string;
	herdId: string;
	originType: "born_on_farm" | "purchased" | "";
};

const NO_CARRIED: CarriedValues = { breed: "", herdId: "", originType: "" };

function NewAnimalPage() {
	const { orgId, establishmentId } = Route.useParams();
	const { tag, herd } = Route.useSearch();
	const { t } = useTranslation();
	const navigate = Route.useNavigate();

	const { data: herdsData } = useQuery(
		listHerdsByOrganizationQueryOptions(orgId, { establishmentId }),
	);

	const { data: animalsData } = useQuery(
		listAnimalsByOrganizationQueryOptions(orgId, {
			establishmentId,
			limit: "1000",
		}),
	);

	const createMutation = useCreateAnimal();
	const createCalvingMutation = useCreateCalving();
	const deleteMutation = useDeleteAnimal();
	const deleteOperationMutation = useDeleteOperation();

	// The sitting: what was created, what the form should carry into the next
	// animal, and whether the confirmation is showing instead of the form.
	const [session, setSession] = useState<CreatedAnimalEntry[]>([]);
	const [confirmed, setConfirmed] = useState<CreatedAnimalEntry | null>(null);
	const [repeat, setRepeat] = useState(true);
	const [carried, setCarried] = useState<CarriedValues>(NO_CARRIED);
	// Bumped whenever the form must start over from scratch, so it remounts with
	// the carried values as its defaults instead of keeping the previous state.
	const [formGeneration, setFormGeneration] = useState(0);

	const herds = (herdsData?.data ?? []) as LivestockHerdWithStats[];
	const animalOptions = (animalsData?.data ?? []).map((a) => ({
		id: a.id,
		tagId: a.tagId,
		sex: a.sex,
		status: a.status,
		herdId: a.herdId,
	}));

	const goToAnimalsList = () =>
		navigate({
			to: "/app/org/$orgId/livestock",
			params: { orgId },
			search: { tab: "animals", farm: establishmentId },
		});

	const openRecord = (animalId: string) =>
		navigate({
			to: "/app/org/$orgId/livestock/$establishmentId/animals/$animalId",
			params: { orgId, establishmentId, animalId },
		});

	/** "Fêmea · Nelore · Lote 7 — Matrizes", or the empty-details fallback. */
	const summarize = (data: CreateAnimalMutationRequest): string => {
		const herdName = data.herdId
			? herds.find((h) => h.id === data.herdId)?.name
			: undefined;
		const parts = [
			data.sex ? t(`livestock.animal.sexes.${data.sex}`) : undefined,
			data.breed || undefined,
			herdName,
		].filter(Boolean);
		return parts.length > 0
			? parts.join(" · ")
			: t("livestock.animal.created.noDetails");
	};

	const recordCreated = (
		data: CreateAnimalMutationRequest,
		animalId: string | undefined,
		addAnother: boolean,
		calvingOperationId?: string,
	) => {
		const entry: CreatedAnimalEntry = {
			id: animalId,
			calvingOperationId,
			tagId: data.tagId,
			summary: summarize(data),
			createdAt: Date.now(),
		};
		setSession((entries) => [entry, ...entries]);
		setCarried({
			breed: data.breed ?? "",
			herdId: data.herdId ?? "",
			originType:
				(data.originType as CarriedValues["originType"] | undefined) ?? "",
		});
		// "Criar e próximo" stays on the form (it has already reset itself);
		// "Criar animal" hands over to the confirmation screen.
		if (!addAnother) setConfirmed(entry);
	};

	const handleSubmit = async (
		formData: CreateAnimalMutationRequest | UpdateAnimalMutationRequest,
		options?: AnimalFormSubmitOptions,
	) => {
		const createData = formData as CreateAnimalMutationRequest;

		// Newborn whose dam is an on-farm cow: the calving flow records the CALVING
		// op on the dam and creates this animal as the calf in one transaction, so
		// the plain create-animal payload is not used. Return the promise so the
		// form's guarded submit spans the request (§3.5) and surfaces failures.
		if (options?.calving) {
			const result = await createCalvingMutation.mutateAsync({
				organizationId: orgId,
				data: options.calving,
			});
			recordCreated(
				createData,
				result?.calf?.id,
				options?.addAnother ?? false,
				result?.operation?.id,
			);
			return result;
		}

		const result = await createMutation.mutateAsync({
			organizationId: orgId,
			data: createData,
		});
		// Offline the mutation resolves with a queue sentinel rather than a row, so
		// there is no id to open or undo until it replays.
		recordCreated(createData, result?.id, options?.addAnother ?? false);
		return result;
	};

	/** Back to a blank form, carrying breed / lot / origin when "Repetir…" is on. */
	const startNextAnimal = () => {
		setConfirmed(null);
		setFormGeneration((generation) => generation + 1);
	};

	const undoLastCreate = async () => {
		if (!confirmed) return;
		const undone = await undoCreatedAnimal(confirmed, orgId, {
			deleteOperation: (input) => deleteOperationMutation.mutateAsync(input),
			deleteAnimal: (input) => deleteMutation.mutateAsync(input),
		});
		if (!undone) return;
		setSession((entries) => entries.slice(1));
		setConfirmed(null);
	};

	if (confirmed) {
		return (
			// `-mt-3` cancels `<main>`'s `p-3` top gutter on phones (see the edit page).
			<div className="-mt-3 flex flex-col gap-2 md:mt-0 md:gap-6">
				<PageHeader
					title={t("livestock.animal.create.title")}
					showBackButton
					onBack={goToAnimalsList}
				/>
				<AnimalCreatedPanel
					last={confirmed}
					session={session}
					repeat={repeat}
					onRepeatChange={setRepeat}
					onNext={startNextAnimal}
					onOpenRecord={openRecord}
					onUndo={undoLastCreate}
					undoPending={
						deleteMutation.isPending || deleteOperationMutation.isPending
					}
				/>
			</div>
		);
	}

	return (
		// `-mt-3` cancels `<main>`'s `p-3` top gutter on phones (see the edit page).
		<div className="-mt-3 flex flex-col gap-2 md:mt-0 md:gap-6">
			<PageHeader title={t("livestock.animal.create.title")} showBackButton />
			<AnimalForm
				key={formGeneration}
				establishmentId={establishmentId}
				orgId={orgId}
				initialTagId={tag}
				initialValues={
					repeat && formGeneration > 0
						? carried
						: herd
							? { herdId: herd }
							: undefined
				}
				repeatOnAddAnother={repeat}
				herds={herds}
				animals={animalOptions}
				onSubmit={handleSubmit}
				isSubmitting={
					createMutation.isPending || createCalvingMutation.isPending
				}
				onViewAnimal={openRecord}
			/>
		</div>
	);
}
